<template>
    <div class="directory" :class="{ 'directory-embedded': embedded }">
        <div class="directory-header">
            <h2 v-if="!embedded && !hideTitle" class="display-small-serif pr-10">{{ t("connections.organizationDirectory.title") }}</h2>
            <p v-if="!embedded" class="body-small text-ink-muted">{{ description }}</p>
            <BaseInput
                v-model="query"
                type="search"
                :aria-label="t('connections.organizationDirectory.searchLabel')"
                :placeholder="t('connections.organizationDirectory.searchLabel')"
                maxlength="200">
                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
            </BaseInput>
        </div>

        <p class="sr-only" role="status">
            {{ isSearching ? t("connections.organizationDirectory.loadingProfiles") : isFetching ? t("connections.organizationDirectory.loadingMoreProfiles") : "" }}
        </p>

        <div ref="results" class="directory-results">
            <BaseCard v-if="!hasCollaborationProfile" padding="sm" class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p class="body-small-semibold">{{ t("connections.organizationDirectory.sharingTitle") }}</p>
                    <p class="body-small text-ink-muted">{{ t("connections.organizationDirectory.sharingBody") }}</p>
                </div>
                <BaseButton theme="secondary" size="sm" :disabled="isBusy" @click="openUrl(createProfileUrl)">{{ t("connections.organizationDirectory.createMine") }}</BaseButton>
            </BaseCard>

            <BaseNotification v-if="loadFailed" theme="error" :text="t('connections.organizationDirectory.loadFailed')" hide-dismiss />
            <BaseButton v-if="loadFailed" theme="neutral" size="sm" data-directory-retry @click="loadPage()">{{ t("common.tryAgain") }}</BaseButton>
            <BaseNotification v-if="addFailed" theme="error" :text="t('connections.organizationDirectory.addFailed')" @dismiss="addFailed = false" />
            <BaseNotification v-if="removeFailed" theme="error" :text="t('connections.organizationDirectory.removeFailed')" @dismiss="removeFailed = false" />
            <BaseNotification v-if="copyFailed" theme="error" :text="t('connections.organizationDirectory.copyFailed')" @dismiss="copyFailed = false" />

            <Transition name="directory-content" mode="out-in">
                <div v-if="isSearching" key="loading">
                    <div aria-hidden="true">
                        <SkeletonLoader v-if="!embedded" class="h-5 w-56 mb-4 rounded motion-reduce:animate-none" />
                        <!-- Embedded on a setup step, a full page of placeholders collapses visibly when the org is small. -->
                        <div v-for="row in embedded ? 3 : 6" :key="row" class="profile-row">
                            <SkeletonLoader class="size-8 shrink-0 rounded-full motion-reduce:animate-none" />
                            <div class="grow space-y-2">
                                <SkeletonLoader class="h-4 w-32 rounded motion-reduce:animate-none" />
                                <SkeletonLoader class="h-3 w-3/4 rounded motion-reduce:animate-none" />
                            </div>
                            <SkeletonLoader class="size-9 shrink-0 rounded-lg motion-reduce:animate-none" />
                        </div>
                    </div>
                </div>
                <div v-else-if="visibleProfiles.length" key="profiles" class="flex flex-col gap-4">
                    <p v-if="!embedded" class="body-small text-ink-muted">{{ t("connections.organizationDirectory.intro") }}</p>
                    <ul :aria-label="t('connections.organizationDirectory.listLabel')">
                        <li v-for="profile in visibleProfiles" :key="profile.user_id" class="profile-row">
                            <CollaborationProfileAvatar size="sm" :name="profile.name" />
                            <div class="min-w-0 grow">
                                <p class="body-small-semibold truncate">{{ profile.name }}</p>
                                <p class="body-small text-ink-muted">
                                    <span v-if="profile.title" class="profile-detail">{{ profile.title }}</span>
                                    <span v-if="profile.email" class="profile-detail">{{ profile.email }}</span>
                                </p>
                            </div>
                            <BaseButton
                                :theme="isAdded(profile) ? 'primary' : 'neutral'"
                                :icon-only="!embedded"
                                class="gap-2"
                                data-directory-add
                                :disabled="isBusy || isLinkedElsewhere(profile)"
                                :aria-pressed="isAdded(profile)"
                                :aria-label="toggleLabel(profile)"
                                @click="toggleProfile(profile)">
                                <LoadingSpinner v-if="isBusy && pendingUserId === profile.user_id" class="size-4" aria-hidden="true" />
                                <i v-else :class="isAdded(profile) ? 'bi bi-check-lg' : 'bi bi-plus-lg'" aria-hidden="true"></i>
                                <span v-if="embedded">{{ isAdded(profile) ? t("connections.organizationDirectory.added") : t("connections.organizationDirectory.add") }}</span>
                            </BaseButton>
                        </li>
                    </ul>
                    <BaseButton v-if="embedded && nextOffset !== null && !loadFailed" theme="neutral" :disabled="isBusy" :aria-disabled="isFetching" @click="loadMoreAndKeepFocus">
                        {{ t("connections.organizationDirectory.loadMore") }}
                    </BaseButton>
                    <p v-if="isFetching" class="body-small text-ink-muted text-center">{{ t("connections.organizationDirectory.loadingMore") }}</p>
                </div>
                <div v-else-if="!loadFailed" key="empty" class="directory-message" data-testid="directory-empty">
                    <i class="bi bi-person-x" aria-hidden="true"></i>
                    <h3 class="display-small-serif">
                        {{ emptyTitle }}
                    </h3>
                    <p v-if="!embedded" class="body-small text-ink-muted">
                        {{ t("connections.organizationDirectory.emptyBody") }}
                    </p>
                    <PublicProfileRequestLinkAction v-if="!embedded" :copied="isCopied" :disabled="isCopying" hide-dismiss @copy="copyRequestLink" />
                </div>
            </Transition>
        </div>

        <div class="directory-footer">
            <p class="body-small text-ink-muted" role="status">
                {{
                    addedCount === null
                        ? ""
                        : addedCount
                          ? t("connections.organizationDirectory.addedCount", { count: addedCount }, addedCount)
                          : t("connections.organizationDirectory.nobodyAdded")
                }}
            </p>
            <BaseButton v-if="showBackToAll" theme="neutral" class="inline-flex items-center gap-2" @click="query = ''">
                <i class="bi bi-arrow-left" aria-hidden="true"></i>
                {{ t("connections.organizationDirectory.backToAll") }}
            </BaseButton>
            <BaseButton v-else-if="!embedded" theme="neutral" :disabled="isBusy" @click="emit('close')">{{
                addedCount ? t("connections.organizationDirectory.doneAdding") : t("common.close")
            }}</BaseButton>
        </div>
        <BaseToast v-model:open="isCopiedToastOpen" :duration="TOAST_DURATION_MS" :description="t('connections.organizationDirectory.linkCopiedToast')" />
    </div>
</template>

<script setup lang="ts">
import { refDebounced, useInfiniteScroll } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { CollabProfileLinkResult, OrganizationProfile, OrganizationProfilesPage, ProfileDirectorySource } from "~vue/api/generated/collab-profiles";
import type { OrganizationDetails } from "~vue/appTypes";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import { logProfileShared, type ProfileShareSource, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import PublicProfileRequestLinkAction from "~vue/Connections/PublicProfileRequestLinkAction.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, type Ref, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { canComparePerspectives } = useProfileSharingCopy();

const props = defineProps<{
    hasCollaborationProfile: boolean;
    createProfileUrl: string;
    inviteUrl: string;
    source: ProfileDirectorySource;
    embedded?: boolean;
    hideTitle?: boolean;
}>();
const inviteShareSource = computed<ProfileShareSource>(() => (props.source === "guided_setup" ? "guided_setup" : "org_directory"));
const emit = defineEmits<{ added: []; removed: []; close: []; busy: [value: boolean]; empty: [value: boolean] }>();
const TOAST_DURATION_MS = 6000;
const description = computed(() =>
    canComparePerspectives.value ? t("connections.organizationDirectory.descriptionWithPerspectives") : t("connections.organizationDirectory.description"),
);
const organization = inject<Ref<OrganizationDetails | null>>("organization_details", ref(null));
const organizationLabel = computed(() => (organization.value && !organization.value.is_default && organization.value.name) || t("connections.organizationDirectory.yourCompany"));

const query = ref("");
const debouncedQuery = refDebounced(query, 300);
const offset = ref(0);
const isNextPage = ref(false);
const nextOffset = ref<number | null>(null);
const profiles = ref<OrganizationProfile[]>([]);
const addedIds = ref(new Set<string>());
/* Profiles toggled in this session stay reachable when a search reload drops them from the API page. */
const sessionProfiles = ref(new Map<string, OrganizationProfile>());
/* Adds that came back `noop` were linked outside this modal, so they are shown as added but are not undoable here. */
const linkedElsewhereIds = ref(new Set<string>());
const createdConnectionIds = ref(new Map<string, string>());
const addedCount = ref<number | null>(null);
const loadFailed = ref(false);
const isSearching = computed(() => query.value !== debouncedQuery.value || (isFetching.value && !isNextPage.value));
const matchesQuery = (profile: OrganizationProfile) => {
    const needle = debouncedQuery.value.trim().toLowerCase();
    return !needle || [profile.name, profile.title, profile.email].some((field) => field?.toLowerCase().includes(needle));
};
const visibleProfiles = computed(() => {
    const loadedIds = new Set(profiles.value.map((profile) => profile.user_id));
    const pinned = [...sessionProfiles.value.values()].filter((profile) => !loadedIds.has(profile.user_id) && matchesQuery(profile));
    return [...pinned, ...profiles.value];
});
const isEmpty = computed(() => !isSearching.value && !loadFailed.value && visibleProfiles.value.length === 0);
const showBackToAll = computed(() => !!query.value.trim() && isEmpty.value);
const emptyTitle = computed(() => {
    if (query.value.trim()) {
        return t("connections.organizationDirectory.noMatchTitle", { query: query.value.trim(), organization: organizationLabel.value });
    }
    return addedCount.value ? t("connections.organizationDirectory.addedEveryone") : t("connections.organizationDirectory.noSharedProfiles");
});
const url = computed(() => {
    const params = new URLSearchParams({ q: debouncedQuery.value.trim(), offset: String(offset.value), exclude_added: "true" });
    if (props.embedded) {
        params.set("limit", "10");
    }
    return `/v1/collaboration-profiles/directory?${params}`;
});
const { data, error, isFetching, execute } = useFetch(url, { immediate: false }).get().json<OrganizationProfilesPage>();
let requestVersion = 0;

async function loadPage() {
    const version = ++requestVersion;
    loadFailed.value = false;
    const response = await execute();
    if (version !== requestVersion) {
        return;
    }
    if (!response?.ok || error.value || !data.value) {
        loadFailed.value = true;
        return;
    }
    profiles.value = isNextPage.value ? [...profiles.value, ...data.value.items] : data.value.items;
    nextOffset.value = data.value.next_offset;
    // Start from all saved links, regardless of the current search or loaded page.
    addedCount.value ??= data.value.added_count;
}

watch(
    debouncedQuery,
    () => {
        offset.value = 0;
        isNextPage.value = false;
        nextOffset.value = null;
        profiles.value = [];
        void loadPage();
    },
    { immediate: true },
);

watch(isEmpty, (value) => emit("empty", value), { immediate: true });

async function loadMore() {
    if (nextOffset.value === null || isFetching.value || isSearching.value || loadFailed.value) {
        return;
    }
    offset.value = nextOffset.value;
    isNextPage.value = true;
    await loadPage();
}

/* Load more is removed once the last page arrives or a load fails, so focus moves to the first new row or the retry. */
async function loadMoreAndKeepFocus() {
    const firstNewIndex = visibleProfiles.value.length;
    await loadMore();
    await nextTick();
    const target = loadFailed.value
        ? results.value?.querySelector<HTMLElement>("[data-directory-retry]")
        : results.value?.querySelectorAll<HTMLElement>("[data-directory-add]")[firstNewIndex];
    target?.focus();
}

const results = useTemplateRef<HTMLElement>("results");
useInfiniteScroll(results, loadMore, {
    distance: 80,
    canLoadMore: () => !props.embedded && nextOffset.value !== null && !isFetching.value && !isSearching.value && !loadFailed.value,
});

const pendingUserId = ref<string | null>(null);
const addUrl = computed(() => `/v1/collaboration-profiles/directory/${pendingUserId.value}?${new URLSearchParams({ source: props.source })}`);
const { data: added, error: addError, isFetching: isAdding, execute: executeAdd } = useFetch(addUrl, { immediate: false }).post().json<CollabProfileLinkResult>();
const removeUrl = computed(() => {
    const createdConnectionId = createdConnectionIds.value.get(pendingUserId.value ?? "");
    const url = `/v1/collaboration-profiles/directory/${pendingUserId.value}`;
    return createdConnectionId ? `${url}?${new URLSearchParams({ created_connection_id: createdConnectionId })}` : url;
});
const { error: removeError, isFetching: isRemoving, execute: executeRemove } = useFetch(removeUrl, { immediate: false }).delete();
const isBusy = computed(() => isAdding.value || isRemoving.value);
watch(isBusy, (value) => emit("busy", value));
const addFailed = ref(false);
const removeFailed = ref(false);
const isAdded = (profile: OrganizationProfile) => addedIds.value.has(profile.user_id);
const isLinkedElsewhere = (profile: OrganizationProfile) => linkedElsewhereIds.value.has(profile.user_id);
const toggleLabel = (profile: OrganizationProfile) => {
    if (!isAdded(profile)) {
        return t("connections.organizationDirectory.addLabel", { add: t("connections.organizationDirectory.add"), name: profile.name });
    }
    if (isLinkedElsewhere(profile)) {
        return t("connections.organizationDirectory.alreadyAddedLabel", { name: profile.name });
    }
    return props.embedded
        ? t("connections.organizationDirectory.addedRemoveLabel", { added: t("connections.organizationDirectory.added"), name: profile.name })
        : t("connections.organizationDirectory.removeLabel", { name: profile.name });
};

async function toggleProfile(profile: OrganizationProfile) {
    if (isBusy.value || isLinkedElsewhere(profile)) {
        return;
    }
    pendingUserId.value = profile.user_id;
    if (isAdded(profile)) {
        await removeProfile(profile);
    } else {
        await addProfile(profile);
    }
}

async function addProfile(profile: OrganizationProfile) {
    addFailed.value = false;
    const response = await executeAdd();
    if (!response?.ok || addError.value || !added.value) {
        addFailed.value = true;
        return;
    }
    addedIds.value.add(profile.user_id);
    sessionProfiles.value.set(profile.user_id, profile);
    if (added.value.action === "noop") {
        linkedElsewhereIds.value.add(profile.user_id);
        return;
    }
    if (added.value.action === "created") {
        createdConnectionIds.value.set(profile.user_id, added.value.connection_id);
    }
    // The directory excludes saved links, so adding a row shifts the next page back by one.
    if (nextOffset.value !== null) {
        nextOffset.value = Math.max(0, nextOffset.value - 1);
    }
    addedCount.value = (addedCount.value ?? 0) + 1;
    emit("added");
}

async function removeProfile(profile: OrganizationProfile) {
    removeFailed.value = false;
    const response = await executeRemove();
    if (!response?.ok || removeError.value) {
        removeFailed.value = true;
        return;
    }
    addedIds.value.delete(profile.user_id);
    createdConnectionIds.value.delete(profile.user_id);
    if (nextOffset.value !== null) {
        nextOffset.value += 1;
    }
    addedCount.value = Math.max((addedCount.value ?? 1) - 1, 0);
    emit("removed");
}

const { show: showCopied, isShowing: isCopied } = useTimedShow({ duration: 10_000 });
const isCopying = ref(false);
const copyFailed = ref(false);
const isCopiedToastOpen = ref(false);
async function copyRequestLink() {
    isCopying.value = true;
    copyFailed.value = false;
    if (await copyShareUrl(withShareAttribution(props.inviteUrl, inviteShareSource.value, "link"), profileInviteMessage())) {
        showCopied();
        isCopiedToastOpen.value = true;
        void logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, { source: props.source });
        void logProfileShared({ shareSource: inviteShareSource.value, target: "link", shareKind: "invite" });
    } else {
        copyFailed.value = true;
    }
    isCopying.value = false;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .directory {
        @apply flex flex-1 flex-col min-h-0 text-ink;
    }
    .directory-embedded {
        @apply rounded-xl border border-border bg-surface-raised;
    }
    .directory-embedded .directory-header {
        @apply border-b-0 pb-0;
    }
    .directory-embedded .directory-results {
        @apply flex-none overflow-visible;
    }
    .directory-embedded .profile-row + .profile-row {
        @apply border-t border-border;
    }
    .directory-embedded .directory-footer {
        @apply flex-wrap py-3;
    }
    .directory-header {
        @apply flex shrink-0 flex-col gap-3 p-5 md:p-6 border-b border-border;
    }
    .directory-results {
        @apply flex flex-1 flex-col gap-4 min-h-0 overflow-y-auto p-5 md:p-6;
    }
    .directory-content-enter-active,
    .directory-content-leave-active {
        @apply transition-opacity duration-150 motion-reduce:transition-none;
    }
    .directory-content-enter-from,
    .directory-content-leave-to {
        @apply opacity-0;
    }
    .profile-row {
        @apply flex items-center gap-3 py-3;
    }
    /* On mobile the title and email get a line each, so truncating a long title never hides the email. */
    .profile-detail {
        @apply block truncate md:inline md:overflow-visible md:whitespace-normal md:break-words;
    }
    .profile-detail + .profile-detail {
        @apply md:before:content-['_·_'];
    }
    .directory-message {
        @apply flex flex-1 flex-col items-center justify-center gap-4 py-6 text-center;
    }
    .directory-footer {
        @apply flex shrink-0 items-center justify-between gap-3 p-5 md:px-6 border-t border-border;
    }
}
</style>
